import {
  MSG_NO_PENDING_ITEMS,
  MSG_NO_UNASSIGNED_ITEMS,
  msgAddedToQueue,
  msgTaskAddedAndAssigned,
  msgTaskAssigned,
} from "@shared/messages";
import {
  type DemoQueueItem,
  findFirstUnassigned,
  findItemByArg,
  formatQueueListSimple,
  truncateText,
} from "@shared/queue-logic";
import {
  ArrowBigRight,
  ArrowLeft,
  Bell,
  Bookmark,
  Hash,
  Home,
  MessageCircle,
  MessageSquareText,
  MoreVertical,
  Search,
  SendHorizontal,
  SmilePlus,
  X,
} from "lucide-react";
import { useActionState, useCallback, useEffect, useRef, useState, useTransition } from "react";
import { useFormStatus } from "react-dom";
import { useScrollPreservation } from "../hooks/useScrollPreservation";
import { ALL_SCENARIOS, CHANNEL_ORDER, type DemoScenario } from "./scenarios";

interface Message {
  id: string;
  user: string;
  userColor: string;
  avatar: string;
  time: string;
  text: string;
  reactions?: { emoji: string; count: number }[];
  isBot?: boolean;
}

interface UserProfile {
  name: string;
  color: string;
  avatar: string;
  title: string;
  bio: string;
}

const USER_PROFILES: Record<string, UserProfile> = {
  Neil: {
    name: "Neil",
    color: "text-blue-600",
    avatar: "/cat_with_glasses.webp",
    title: "Staff Engineer",
    bio: "His 'patterns to avoid' documentation has 47 entries. His own recent commits account for 12 of them.",
  },
  Sarah: {
    name: "Sarah",
    color: "text-emerald-700",
    avatar: "/woman_avatar.webp",
    title: "Full-Stack Developer",
    bio: "Writes bugs at 2am and fixes them at 2:15am. Has strong opinions on semicolons.",
  },
  Jayden: {
    name: "Jayden",
    color: "text-purple-600",
    avatar: "/man_avatar.webp",
    title: "Senior Developer",
    bio: "Pretends to understand distributed systems. Secretly still uses console.log for debugging.",
  },
  Jordan: {
    name: "Jordan",
    color: "text-indigo-600",
    avatar: "/man_avatar_2.webp",
    title: "Product Designer",
    bio: "Talks about 'the user journey' unironically. Has 47 browser tabs open for 'inspiration'.",
  },
  Casey: {
    name: "Casey",
    color: "text-pink-600",
    avatar: "/woman_avatar_2.webp",
    title: "Marketing Designer",
    bio: "Makes things 'pop' and 'sizzle'. Believes every blog post deserves its own custom illustration.",
  },
  Robert: {
    name: "Robert",
    color: "text-teal-600",
    avatar: "/man_avatar_3.webp",
    title: "Customer Success Manager",
    bio: "Knows every client's dog's name. Can defuse angry emails with a single emoji.",
  },
  Morgan: {
    name: "Morgan",
    color: "text-orange-500",
    avatar: "/woman_avatar_3.webp",
    title: "Customer Success Specialist",
    bio: "Converts trial users by sheer enthusiasm. Has a spreadsheet for everything.",
  },
  Marcus: {
    name: "Marcus",
    color: "text-cyan-600",
    avatar: "/man_avatar_4.webp",
    title: "IT Support",
    bio: "Fixes printers with percussive maintenance. Has seen things you wouldn't believe.",
  },
  You: {
    name: "You",
    color: "text-zinc-600",
    avatar: "Y",
    title: "Queue Master",
    bio: "The hero this team needs, but probably doesn't deserve.",
  },
  Kyubot: {
    name: "Kyubot",
    color: "text-orange-700",
    avatar: "/logo.webp",
    title: "Queue Manager",
    bio: "Making sure your requests don't die in Slack like your houseplants.",
  },
};

interface Thread {
  parentId: string;
  replies: Message[];
}

interface ChannelState {
  messages: Message[];
  threads: Record<string, Thread>;
  queue: DemoQueueItem[];
  messageToQueueItem: Record<string, string>;
  userReactions: Set<string>;
  totalItemsAdded: number;
  busyDaySaid: boolean;
  listCommandUsed: boolean;
  firstCompletionCelebrated: boolean;
  queueClearedCelebrated: boolean;
  userHasInteracted: boolean;
  asyncRequestSent: boolean;
}

function MainSendButton({ disabled }: { disabled: boolean }) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={disabled || pending}
      className="w-8 h-8 p-1.5 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors disabled:opacity-50"
      aria-label="Send message"
    >
      <SendHorizontal className="w-5 h-5" />
    </button>
  );
}

function ThreadSendButton({ disabled }: { disabled: boolean }) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={disabled || pending}
      className="w-8 h-8 p-1.5 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors disabled:opacity-50"
      aria-label="Send reply"
    >
      <SendHorizontal className="w-5 h-5" />
    </button>
  );
}

const formatTime = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `${displayHours}:${displayMinutes} ${ampm}`;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const parseMarkdown = (text: string, _onMentionClick?: (username: string) => void) => {
  // Convert \n to <br> to preserve line breaks
  let result = text.replace(/\n/g, "<br>");
  // Convert ~text~ to <s>text</s> (strikethrough)
  result = result.replace(/~([^~]+)~/g, "<s>$1</s>");
  // Convert `code` to <code>code</code> - escape HTML entities first
  result = result.replace(/`([^`]+)`/g, (_, code) => {
    const escaped = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return `<code class="bg-slate-100 px-1 py-0.5 rounded text-orange-700 font-mono text-sm border border-slate-200">${escaped}</code>`;
  });
  // Convert *text* to <strong>text</strong>
  result = result.replace(/\*([^*]+)\*/g, "<strong>$1</strong>");
  // Convert <@Username> to clickable mention links
  result = result.replace(/<@(\w+)>/g, (_, username) => {
    const profile = USER_PROFILES[username];
    const colorClass = profile?.color || "text-blue-600";
    return `<span class="mention-link ${colorClass} bg-blue-50 px-1 rounded cursor-pointer hover:underline font-medium" data-username="${username}">@${username}</span>`;
  });
  // Convert URLs to clickable links (handles http://, https://, and www.)
  result = result.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
  );
  result = result.replace(
    /(^|[\s>])(www\.[^\s<]+)/g,
    '$1<a href="https://$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$2</a>'
  );
  return result;
};

const HELP_TEXT = `*Kyubot Commands:*
React 🎟️ to add, ✅ to complete, 🙋 to take
\`!add <item>\` - Add an item to the queue
\`!list\` - Show all items in the queue
\`!done\` - Mark first item as done
\`!done <n|text>\` - Mark specific item by number or matching text
\`!take\` - Assign current thread item to you`;

// Heuristic to determine if a message looks like a request worth queuing
const looksLikeRequest = (text: string): boolean => {
  const lower = text.toLowerCase().trim();

  // Keywords that suggest a request - check this first
  const requestKeywords = [
    "help",
    "need",
    "can someone",
    "can anyone",
    "please",
    "access",
    "fix",
    "issue",
    "problem",
    "broken",
    "error",
    "not working",
    "reset",
    "password",
    "permission",
    "install",
    "setup",
    "configure",
    "review",
    "approve",
    "update",
    "check",
    "look at",
    "assist",
  ];
  if (requestKeywords.some((kw) => lower.includes(kw))) return true;

  // Questions are often requests
  if (text.includes("?")) return true;

  // Too short without keywords - probably not a request
  if (lower.length < 15) return false;

  // Pure casual messages (no request keywords already checked above)
  const pureCasual = [
    /^(hi|hey|hello|yo|sup)(\s.*)?$/,
    /^(thanks|thank you|thx|ty)(\s.*)?$/,
    /^(ok|okay|sure|np|no problem)(\s.*)?$/,
    /^(bye|goodbye|later|cya)(\s.*)?$/,
    /^(yes|no|yep|nope|yeah|nah)(\s.*)?$/,
    /^(nice|cool|awesome|great|lol|haha|lmao)(\s.*)?$/,
  ];
  if (pureCasual.some((p) => p.test(lower))) return false;

  // Longer messages (20+ chars) without casual patterns are likely requests
  return lower.length >= 20;
};

// Empty initial threads for prevThreadsRef
const emptyThreads: Record<string, Thread> = {};

// Initialize channel state from a scenario
const initializeChannelState = (scenario: DemoScenario): ChannelState => {
  const messages: Message[] = scenario.initialMessages.map((m) => ({
    id: m.id,
    user: m.user,
    userColor: m.userColor,
    avatar: m.avatar,
    time: m.time,
    text: m.text,
    reactions: m.reactions,
    isBot: m.isBot,
  }));

  const threads: Record<string, Thread> = {};
  for (const [parentId, thread] of Object.entries(scenario.initialThreads)) {
    threads[parentId] = {
      parentId: thread.parentId,
      replies: thread.replies.map((r) => ({
        id: r.id,
        user: r.user,
        userColor: r.userColor,
        avatar: r.avatar,
        time: r.time,
        text: r.text,
        isBot: r.isBot,
      })),
    };
  }

  const queue: DemoQueueItem[] = scenario.initialQueue.map((item) => ({
    id: item.id,
    text: item.text,
    addedBy: item.addedBy,
    addedByName: item.addedByName,
    addedAt: new Date().toISOString(),
    assignedTo: item.assignedTo,
    assignedToName: item.assignedToName,
    done: false,
    botMessageId: item.botMessageId,
    assignMessageId: item.assignMessageId,
    sourceMessageId: Object.keys(scenario.initialMessageToQueueItem).find(
      (msgId) => scenario.initialMessageToQueueItem[msgId] === item.id
    ),
    requestedBy: item.addedBy,
  }));

  return {
    messages,
    threads,
    queue,
    messageToQueueItem: { ...scenario.initialMessageToQueueItem },
    userReactions: new Set(),
    totalItemsAdded: queue.length,
    busyDaySaid: false,
    listCommandUsed: false,
    firstCompletionCelebrated: false,
    queueClearedCelebrated: false,
    userHasInteracted: false,
    asyncRequestSent: false,
  };
};

// NPC message creator (outside component for stable reference)
const createNpcMessage = (user: string, text: string, avatar: string, color: string): Message => ({
  id: `npc-${Date.now()}-${Math.random()}`,
  user,
  userColor: color,
  avatar,
  time: formatTime(new Date()),
  text,
});

export function ThreadedDemo() {
  // Multi-channel state management
  const [activeChannel, setActiveChannel] = useState<string>(CHANNEL_ORDER[0]);
  const activeChannelRef = useRef(activeChannel);
  activeChannelRef.current = activeChannel;
  const [viewedChannels, setViewedChannels] = useState<Set<string>>(
    () => new Set([CHANNEL_ORDER[0]])
  );
  const [, startTransition] = useTransition();
  const [channelStates, setChannelStates] = useState<Record<string, ChannelState>>(() => {
    const states: Record<string, ChannelState> = {};
    for (const channelId of CHANNEL_ORDER) {
      states[channelId] = initializeChannelState(ALL_SCENARIOS[channelId]);
    }
    return states;
  });

  // Get current channel's state
  const currentState = channelStates[activeChannel];
  const currentScenario = ALL_SCENARIOS[activeChannel];

  // Helper to update current channel's state
  const updateCurrentState = (updater: (state: ChannelState) => ChannelState) => {
    setChannelStates((prev) => ({
      ...prev,
      [activeChannel]: updater(prev[activeChannel]),
    }));
  };

  // Derived state from current channel
  const messages = currentState.messages;
  const threads = currentState.threads;
  const queue = currentState.queue;
  const userReactions = currentState.userReactions;
  const messageToQueueItem = currentState.messageToQueueItem;
  const totalItemsAdded = currentState.totalItemsAdded;
  const busyDaySaid = currentState.busyDaySaid;
  const listCommandUsed = currentState.listCommandUsed;
  const firstCompletionCelebrated = currentState.firstCompletionCelebrated;
  const queueClearedCelebrated = currentState.queueClearedCelebrated;
  const userHasInteracted = currentState.userHasInteracted;
  const asyncRequestSent = currentState.asyncRequestSent;

  // State setters that update the current channel
  const setMessages = (updater: Message[] | ((prev: Message[]) => Message[])) => {
    updateCurrentState((state) => ({
      ...state,
      messages: typeof updater === "function" ? updater(state.messages) : updater,
    }));
  };
  const setThreads = (
    updater: Record<string, Thread> | ((prev: Record<string, Thread>) => Record<string, Thread>)
  ) => {
    updateCurrentState((state) => ({
      ...state,
      threads: typeof updater === "function" ? updater(state.threads) : updater,
    }));
  };
  const setQueue = (updater: DemoQueueItem[] | ((prev: DemoQueueItem[]) => DemoQueueItem[])) => {
    updateCurrentState((state) => ({
      ...state,
      queue: typeof updater === "function" ? updater(state.queue) : updater,
    }));
  };
  const setUserReactions = (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    updateCurrentState((state) => ({
      ...state,
      userReactions: typeof updater === "function" ? updater(state.userReactions) : updater,
    }));
  };
  const setMessageToQueueItem = (
    updater: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => {
    updateCurrentState((state) => ({
      ...state,
      messageToQueueItem:
        typeof updater === "function" ? updater(state.messageToQueueItem) : updater,
    }));
  };
  const setTotalItemsAdded = (updater: number | ((prev: number) => number)) => {
    updateCurrentState((state) => ({
      ...state,
      totalItemsAdded: typeof updater === "function" ? updater(state.totalItemsAdded) : updater,
    }));
  };
  const setBusyDaySaid = (value: boolean) => {
    updateCurrentState((state) => ({ ...state, busyDaySaid: value }));
  };
  const setListCommandUsed = (value: boolean) => {
    updateCurrentState((state) => ({ ...state, listCommandUsed: value }));
  };
  const setFirstCompletionCelebrated = (value: boolean) => {
    updateCurrentState((state) => ({ ...state, firstCompletionCelebrated: value }));
  };
  const setQueueClearedCelebrated = (value: boolean) => {
    updateCurrentState((state) => ({ ...state, queueClearedCelebrated: value }));
  };
  const setUserHasInteracted = (value: boolean) => {
    updateCurrentState((state) => ({ ...state, userHasInteracted: value }));
  };
  const setAsyncRequestSent = (value: boolean) => {
    updateCurrentState((state) => ({ ...state, asyncRequestSent: value }));
  };

  const [activeThread, setActiveThread] = useState<string | null>(null);
  const [activeProfile, setActiveProfile] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<"home" | "chat">("chat");
  const [longPressedMessage, setLongPressedMessage] = useState<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const [threadInput, setThreadInput] = useState("");
  const [mainInput, setMainInput] = useState("");
  const [mainTypingUser, setMainTypingUser] = useState<string | null>(null); // Track who is typing in main channel
  const [threadTypingUser, setThreadTypingUser] = useState<string | null>(null); // Track who is typing in thread
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const threadContainerRef = useRef<HTMLDivElement>(null);

  const [pendingThreadReply, setPendingThreadReply] = useState<{
    threadId: string;
    reply: Message;
  } | null>(null);

  const { captureScrollState, skipNextScroll, resetSkipNextScroll } = useScrollPreservation(
    messagesContainerRef,
    activeThread,
    messages
  );

  // Helper to check if message overflows and scroll if needed (for main channel messages)
  const checkMessageOverflow = (messageId: string) => {
    requestAnimationFrame(() => {
      const msgElement = document.getElementById(`msg-${messageId}`);
      const container = messagesContainerRef.current;
      if (msgElement && container) {
        const elementBottom = msgElement.offsetTop + msgElement.offsetHeight;
        const viewportBottom = container.scrollTop + container.clientHeight;
        if (elementBottom > viewportBottom) {
          container.scrollTop += elementBottom - viewportBottom;
        }
      }
    });
  };

  // Wrapper to save scroll position before opening sidebar
  const openThread = (threadId: string | null) => {
    if (threadId !== activeThread) {
      captureScrollState();
    }
    setActiveThread(threadId);
    // Close profile sidebar when opening a thread
    if (threadId) {
      setActiveProfile(null);
    }
  };

  // Profile sidebar handlers
  const openProfile = useCallback((username: string) => {
    if (USER_PROFILES[username]) {
      setActiveProfile(username);
    }
  }, []);

  const closeProfile = useCallback(() => {
    setActiveProfile(null);
  }, []);

  const backToThread = useCallback(() => {
    setActiveProfile(null);
  }, []);

  // Handle mention clicks in message content
  const handleMessageClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("mention-link")) {
        const mentionedUser = target.getAttribute("data-username");
        if (mentionedUser) {
          openProfile(mentionedUser);
        }
      }
    },
    [openProfile]
  );

  const createBotMessage = (text: string): Message => ({
    id: `bot-${Date.now()}-${Math.random()}`,
    user: "Kyubot",
    userColor: "text-orange-700",
    avatar: "/logo.webp",
    time: formatTime(new Date()),
    text,
    isBot: true,
  });

  const prevMessagesLength = useRef(messages.length);

  useEffect(() => {
    // Only scroll if we were already at the bottom or it's a new message from 'You'
    if (messages.length > prevMessagesLength.current) {
      const lastMsg = messages[messages.length - 1];
      // Check if we should skip smooth scrolling (e.g. after layout change)
      const behavior = skipNextScroll ? "auto" : "smooth";
      resetSkipNextScroll();

      if (lastMsg?.user === "You") {
        messagesEndRef.current?.scrollIntoView({ behavior, block: "nearest" });
      } else {
        // Optional: Smart auto-scroll for others could go here
        // For now, simple behavior for demo
        messagesEndRef.current?.scrollIntoView({ behavior, block: "nearest" });
      }
    }
    prevMessagesLength.current = messages.length;
  }, [messages, skipNextScroll, resetSkipNextScroll]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run when activeThread/threads change to scroll thread panel
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "auto", block: "nearest" });
  }, [activeThread, threads]);

  // Systematic fix: When threads change, check if any parent message overflows
  const prevThreadsRef = useRef<Record<string, Thread>>(emptyThreads);
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevThreadsRef.current = threads;
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    // Find which thread IDs have new/more replies
    const prevThreads = prevThreadsRef.current;
    for (const threadId of Object.keys(threads)) {
      const prevReplyCount = prevThreads[threadId]?.replies.length || 0;
      const newReplyCount = threads[threadId]?.replies.length || 0;

      // If replies were added, check if parent message overflows
      if (newReplyCount > prevReplyCount) {
        requestAnimationFrame(() => {
          const msgElement = document.getElementById(`msg-${threadId}`);
          if (msgElement && container) {
            const elementBottom = msgElement.offsetTop + msgElement.offsetHeight;
            const viewportBottom = container.scrollTop + container.clientHeight;

            if (elementBottom > viewportBottom) {
              container.scrollTop += elementBottom - viewportBottom;
            }
          }
        });
      }
    }

    prevThreadsRef.current = threads;
  }, [threads]);

  // Async request - 10s after user first interacts
  // No cleanup: timers must survive re-renders caused by setAsyncRequestSent.
  // We use setChannelStates directly (stable reference) with a captured channelId
  // so the message targets the correct channel even if the user switches tabs.
  // Typing indicator only shows when the user is viewing the originating channel.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setChannelStates is stable; activeChannel captured in closure
  useEffect(() => {
    if (userHasInteracted && !asyncRequestSent && currentScenario.asyncRequest) {
      setAsyncRequestSent(true);
      const { user, text } = currentScenario.asyncRequest;
      const profile = USER_PROFILES[user];
      const channelId = activeChannel;
      setTimeout(() => {
        if (activeChannelRef.current === channelId) {
          setMainTypingUser(user);
        }
        setTimeout(() => {
          setMainTypingUser((prev) => (prev === user ? null : prev));
          const asyncMsg: Message = {
            id: `async-${Date.now()}`,
            user,
            userColor: profile?.color ?? "text-blue-600",
            avatar: profile?.avatar ?? "Y",
            time: formatTime(new Date()),
            text,
          };
          setChannelStates((prev) => ({
            ...prev,
            [channelId]: {
              ...prev[channelId],
              messages: [...prev[channelId].messages, asyncMsg],
            },
          }));
        }, 2000);
      }, 8000);
    }
  }, [userHasInteracted, asyncRequestSent]);

  // "Busy day!" when 5 items added (ever)
  useEffect(() => {
    if (totalItemsAdded === 5 && !busyDaySaid) {
      setBusyDaySaid(true);
      const responder = currentScenario.responders[0];
      setTimeout(() => {
        setMessages((msgs) => [
          ...msgs,
          createNpcMessage(responder.name, "Busy day! 😅", responder.avatar, responder.color),
        ]);
      }, 1000);
    }
  }, [totalItemsAdded, busyDaySaid, currentScenario.responders, setBusyDaySaid, setMessages]);

  // Helper to handle queue item completion (shared logic for !done, ✅ emoji, and scenarios)
  // skipReaction: true when user already added ✅ by clicking the emoji
  const handleItemCompletion = async (
    item: DemoQueueItem,
    messageId: string,
    isUserAction = true,
    skipReaction = false
  ) => {
    const displayText = truncateText(item.text);

    // Remove from queue
    setQueue((prev) => prev.filter((q) => q.id !== item.id));

    // Add ✅ reaction to the message (skip if user already added it)
    if (!skipReaction) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, reactions: [...(msg.reactions || []), { emoji: "✅", count: 1 }] }
            : msg
        )
      );
    }

    // Find and remove the message-to-queue mapping
    const originalMessageId = Object.keys(messageToQueueItem).find(
      (msgId) => messageToQueueItem[msgId] === item.id
    );
    if (originalMessageId) {
      // Add ✅ to original message if different from the one we're completing from
      if (!skipReaction && originalMessageId !== messageId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === originalMessageId
              ? { ...msg, reactions: [...(msg.reactions || []), { emoji: "✅", count: 1 }] }
              : msg
          )
        );
      }
      setMessageToQueueItem((prev) => {
        const newMapping = { ...prev };
        delete newMapping[originalMessageId];
        return newMapping;
      });
    }

    // Edit bot's confirmation message with strikethrough
    if (item.botMessageId) {
      setThreads((prev) => {
        const updated = { ...prev };
        for (const [threadId, thread] of Object.entries(updated)) {
          const replyIndex = thread.replies.findIndex((r) => r.id === item.botMessageId);
          if (replyIndex !== -1) {
            updated[threadId] = {
              ...thread,
              replies: thread.replies.map((reply, i) =>
                i === replyIndex
                  ? { ...reply, text: `~Added to queue: "${displayText}"~\nMarked complete.` }
                  : reply
              ),
            };
          }
        }
        return updated;
      });
    }

    // Edit assign message with strikethrough
    if (item.assignMessageId) {
      setThreads((prev) => {
        const updated = { ...prev };
        for (const [threadId, thread] of Object.entries(updated)) {
          const replyIndex = thread.replies.findIndex((r) => r.id === item.assignMessageId);
          if (replyIndex !== -1) {
            updated[threadId] = {
              ...thread,
              replies: thread.replies.map((reply, i) =>
                i === replyIndex
                  ? {
                      ...reply,
                      text: `~Task assigned to ${item.assignedToName || item.assignedTo || "someone"}~\nMarked complete.`,
                    }
                  : reply
              ),
            };
          }
        }
        return updated;
      });
    }

    // Find thread ID for this item
    let threadId = messageId;
    for (const [tid, thread] of Object.entries(threads)) {
      if (
        thread.replies.some((r) => r.id === item.botMessageId) ||
        thread.replies.some((r) => r.id === item.assignMessageId)
      ) {
        threadId = tid;
        break;
      }
    }

    // Notify the original requester in the thread
    const mention =
      item.requestedBy && item.requestedBy !== "user" ? `<@${item.requestedBy}> ` : "";
    setThreads((prev) => {
      const existingThread = prev[threadId];
      if (!existingThread) {
        return {
          ...prev,
          [threadId]: {
            parentId: threadId,
            replies: [
              {
                ...createBotMessage(`${mention}Your request is complete: "${displayText}"`),
              },
            ],
          },
        };
      }
      return {
        ...prev,
        [threadId]: {
          ...existingThread,
          replies: [
            ...existingThread.replies,
            {
              ...createBotMessage(`${mention}Your request is complete: "${displayText}"`),
            },
          ],
        },
      };
    });
    openThread(threadId);

    // Requester says thanks if they're an NPC
    const requesterSaidThanks =
      item.requestedBy && item.requestedBy !== "user" && USER_PROFILES[item.requestedBy];
    if (requesterSaidThanks) {
      const requester = item.requestedBy as string;
      const profile = USER_PROFILES[requester];
      await delay(1500);
      setMainTypingUser(requester);
      await delay(1500);
      setMainTypingUser(null);
      setMessages((prev) => [
        ...prev,
        createNpcMessage(requester, "Thanks! 🙏", profile.avatar, profile.color),
      ]);
    }

    // First completion celebration (only for user actions, skip if requester already responded)
    if (isUserAction && !firstCompletionCelebrated && !requesterSaidThanks) {
      setFirstCompletionCelebrated(true);
      const celebrator = currentScenario.responders[0];
      await delay(800);
      setMessages((prev) => [
        ...prev,
        createNpcMessage(celebrator.name, "Nice one! 💪", celebrator.avatar, celebrator.color),
      ]);
    }

    // Queue cleared celebration (only for user actions)
    const queueLengthAfter = queue.length - 1;
    if (isUserAction && queueLengthAfter === 0 && !queueClearedCelebrated) {
      setQueueClearedCelebrated(true);
      const clearer = currentScenario.responders[1] ?? currentScenario.responders[0];
      await delay(1200);
      setMainTypingUser(clearer.name);
      await delay(2500);
      setMainTypingUser(null);
      setMessages((prev) => [
        ...prev,
        createNpcMessage(
          clearer.name,
          "Yay we cleared the queue! 🎉",
          clearer.avatar,
          clearer.color
        ),
      ]);
    }
  };

  const addReaction = (messageId: string, emoji: string) => {
    const reactionKey = `${messageId}-${emoji}`;
    const hasReacted = userReactions.has(reactionKey);

    // Find the message in main channel or thread replies
    const mainMessage = messages.find((m) => m.id === messageId);
    let threadInfo: { threadId: string; reply: Message } | null = null;

    if (!mainMessage) {
      // Search in thread replies
      for (const [threadId, thread] of Object.entries(threads)) {
        const reply = thread.replies.find((r) => r.id === messageId);
        if (reply) {
          threadInfo = { threadId, reply };
          break;
        }
      }
    }

    const targetMsg = mainMessage || threadInfo?.reply;
    if (!targetMsg) return;

    // Update message reactions
    if (mainMessage) {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== messageId) return msg;
          const existingReaction = msg.reactions?.find((r) => r.emoji === emoji);

          const updatedMsg = hasReacted
            ? existingReaction && existingReaction.count > 1
              ? {
                  ...msg,
                  reactions: msg.reactions?.map((r) =>
                    r.emoji === emoji ? { ...r, count: r.count - 1 } : r
                  ),
                }
              : { ...msg, reactions: msg.reactions?.filter((r) => r.emoji !== emoji) }
            : existingReaction
              ? {
                  ...msg,
                  reactions: msg.reactions?.map((r) =>
                    r.emoji === emoji ? { ...r, count: r.count + 1 } : r
                  ),
                }
              : { ...msg, reactions: [...(msg.reactions || []), { emoji, count: 1 }] };

          // Check if message is now partially out of view and scroll if needed
          setTimeout(() => {
            const msgElement = document.getElementById(`msg-${messageId}`);
            const container = messagesContainerRef.current;
            if (msgElement && container) {
              const elementBottom = msgElement.offsetTop + msgElement.offsetHeight;
              const viewportBottom = container.scrollTop + container.clientHeight;

              // If message bottom is below viewport, scroll just enough to show it fully
              if (elementBottom > viewportBottom) {
                const scrollNeeded = elementBottom - viewportBottom;
                container.scrollTop += scrollNeeded;
              }
            }
          }, 0);

          return updatedMsg;
        })
      );
    } else if (threadInfo) {
      setThreads((prev) => ({
        ...prev,
        [threadInfo.threadId]: {
          ...prev[threadInfo.threadId],
          replies: prev[threadInfo.threadId].replies.map((reply) => {
            if (reply.id !== messageId) return reply;
            const existingReaction = reply.reactions?.find((r) => r.emoji === emoji);

            const updatedReply = hasReacted
              ? existingReaction && existingReaction.count > 1
                ? {
                    ...reply,
                    reactions: (reply.reactions || []).map((r) =>
                      r.emoji === emoji ? { ...r, count: r.count - 1 } : r
                    ),
                  }
                : { ...reply, reactions: (reply.reactions || []).filter((r) => r.emoji !== emoji) }
              : existingReaction
                ? {
                    ...reply,
                    reactions: (reply.reactions || []).map((r) =>
                      r.emoji === emoji ? { ...r, count: r.count + 1 } : r
                    ),
                  }
                : { ...reply, reactions: [...(reply.reactions || []), { emoji, count: 1 }] };

            // Check if thread reply reaction affects parent message overflow in main channel
            setTimeout(() => {
              // First check thread container scroll (for replies within thread)
              const threadContainer = threadContainerRef.current;
              if (threadContainer) {
                const replyElements = threadContainer.querySelectorAll(
                  '[id^="msg-"], [id^="bot-"]'
                );
                const replyElement = Array.from(replyElements).find((el) =>
                  el.textContent?.includes(reply.text.substring(0, 20))
                );

                if (replyElement) {
                  const elementBottom =
                    (replyElement as HTMLElement).offsetTop +
                    (replyElement as HTMLElement).offsetHeight;
                  const viewportBottom = threadContainer.scrollTop + threadContainer.clientHeight;

                  if (elementBottom > viewportBottom) {
                    const scrollNeeded = elementBottom - viewportBottom;
                    threadContainer.scrollTop += scrollNeeded;
                  }
                }
              }

              // Also check if parent message overflows in main channel
              if (threadInfo) {
                checkMessageOverflow(threadInfo.threadId);
              }
            }, 0);

            return updatedReply;
          }),
        },
      }));
    }

    // Toggle user's reaction tracking
    setUserReactions((prev) => {
      const newSet = new Set(prev);
      if (hasReacted) {
        newSet.delete(reactionKey);
      } else {
        newSet.add(reactionKey);
      }
      return newSet;
    });

    if (emoji === "🎟️" && !hasReacted) {
      (async () => {
        await delay(500);
        // Use fresh state to get the updated message with reaction
        const freshMainMessage = messages.find((m) => m.id === messageId);
        let freshThreadInfo: { threadId: string; reply: Message } | null = null;

        if (!freshMainMessage) {
          for (const [tid, thread] of Object.entries(threads)) {
            const reply = thread.replies.find((r) => r.id === messageId);
            if (reply) {
              freshThreadInfo = { threadId: tid, reply };
              break;
            }
          }
        }

        const freshTargetMsg = freshMainMessage || freshThreadInfo?.reply;
        if (!freshTargetMsg) return;

        // Reject command messages - they shouldn't be added to queue via reaction
        if (/^!(add|list|done|take|assign|help)(?:\s|$)/.test(freshTargetMsg.text)) {
          return;
        }

        // Add to queue
        const botMessage = createBotMessage(msgAddedToQueue(freshTargetMsg.text));
        const newItem: DemoQueueItem = {
          id: `queue-${Date.now()}`,
          text: freshTargetMsg.text,
          addedBy: freshTargetMsg.user,
          addedByName: freshTargetMsg.user,
          addedAt: new Date().toISOString(),
          done: false,
          botMessageId: botMessage.id,
          sourceMessageId: messageId,
          requestedBy: freshTargetMsg.user === "You" ? "user" : freshTargetMsg.user,
        };
        setQueue((prev) => [...prev, newItem]);

        // Track mapping from message to queue item
        setMessageToQueueItem((prev) => ({ ...prev, [messageId]: newItem.id }));

        // Determine the thread ID to use
        const threadId = freshThreadInfo?.threadId || messageId;

        // Add bot message to thread
        setThreads((prev) => {
          const existingThread = prev[threadId];
          const existingReplies = existingThread?.replies || [];
          return {
            ...prev,
            [threadId]: {
              parentId: threadId,
              replies: [...existingReplies, botMessage],
            },
          };
        });
        openThread(threadId);
      })();
    }

    if (emoji === "✅" && !hasReacted) {
      (async () => {
        await delay(500);
        // Find the queue item for this message
        const queueItemId = messageToQueueItem[messageId];
        if (!queueItemId) return;

        // Find the item in the queue
        const item = queue.find((q) => q.id === queueItemId);
        if (!item) return;

        // Use shared completion logic (skipReaction=true since user already added ✅)
        handleItemCompletion(item, messageId, true, true);
      })();
    }

    if (emoji === "🎟️" && hasReacted) {
      // User removed their 🎟️ reaction - remove from queue
      (async () => {
        await delay(500);
        const queueItemId = messageToQueueItem[messageId];

        if (queueItemId) {
          const item = queue.find((i) => i.id === queueItemId);
          if (!item) return;

          // Remove from queue
          setQueue((prev) => prev.filter((i) => i.id !== queueItemId));

          // Remove mapping
          setMessageToQueueItem((prev) => {
            const newMapping = { ...prev };
            delete newMapping[messageId];
            return newMapping;
          });

          // Find the thread ID for this message
          let threadId = messageId;
          for (const [tid, thread] of Object.entries(threads)) {
            if (thread.replies.some((r) => r.id === messageId)) {
              threadId = tid;
              break;
            }
          }

          // Update bot message to show removal
          const displayText = truncateText(item.text);
          const existingThread = threads[threadId];
          if (existingThread && existingThread.replies.length > 0) {
            // Find the last bot message in this thread and update it
            let lastBotReplyIndex = -1;
            for (let i = existingThread.replies.length - 1; i >= 0; i--) {
              if (existingThread.replies[i].isBot) {
                lastBotReplyIndex = i;
                break;
              }
            }
            if (lastBotReplyIndex !== -1) {
              setThreads((prev) => ({
                ...prev,
                [threadId]: {
                  ...prev[threadId],
                  replies: prev[threadId].replies.map((reply, i) =>
                    i === lastBotReplyIndex
                      ? {
                          ...reply,
                          text: `~Added to queue: "${displayText}"~\nRemoved from queue.`,
                        }
                      : reply
                  ),
                },
              }));
            }
          }
        }
      })();
    }

    if (emoji === "🙋" && !hasReacted) {
      // Person raising hand emoji - trigger take logic
      (async () => {
        await delay(600);
        // Find the message in main channel or thread replies
        const freshMainMessage = messages.find((m) => m.id === messageId);
        let freshThreadInfo: { threadId: string; reply: Message } | null = null;

        if (!freshMainMessage) {
          for (const [tid, thread] of Object.entries(threads)) {
            const reply = thread.replies.find((r) => r.id === messageId);
            if (reply) {
              freshThreadInfo = { threadId: tid, reply };
              break;
            }
          }
        }

        const freshTargetMsg = freshMainMessage || freshThreadInfo?.reply;
        if (!freshTargetMsg) return;

        // Check if parent message is already in queue
        const existingItem = queue.find((q) => q.text === freshTargetMsg.text);

        if (!existingItem) {
          // Add parent message to queue and track the index
          const assignMessage = createBotMessage(msgTaskAddedAndAssigned("You"));
          const newItem: DemoQueueItem = {
            id: `queue-${Date.now()}`,
            text: freshTargetMsg.text,
            addedBy: freshTargetMsg.user,
            addedByName: freshTargetMsg.user,
            addedAt: new Date().toISOString(),
            done: false,
            assignMessageId: assignMessage.id,
            sourceMessageId: messageId,
            requestedBy: freshTargetMsg.user === "You" ? "user" : freshTargetMsg.user,
          };
          setQueue((prev) => {
            const newQueue = [...prev, newItem];
            const itemIndex = newQueue.length - 1;

            // Assign to you in the same update
            const assignedQueue = newQueue.map((item, i) =>
              i === itemIndex ? { ...item, assignedTo: "You", assignedToName: "You" } : item
            );

            // Add ✅ emoji and bot message
            const threadId = freshThreadInfo?.threadId || messageId;
            setThreads((prev) => {
              if (!prev[threadId]) {
                return {
                  ...prev,
                  [threadId]: {
                    parentId: threadId,
                    replies: [
                      {
                        ...createBotMessage(msgTaskAddedAndAssigned("You")),
                      },
                    ],
                  },
                };
              }
              return {
                ...prev,
                [threadId]: {
                  ...prev[threadId],
                  replies: [
                    ...prev[threadId].replies.map((reply) =>
                      reply.id === messageId
                        ? {
                            ...reply,
                            reactions: [...(reply.reactions || []), { emoji: "✅", count: 1 }],
                          }
                        : reply
                    ),
                    {
                      ...createBotMessage(msgTaskAddedAndAssigned("You")),
                    },
                  ],
                },
              };
            });

            return assignedQueue;
          });
          setMessageToQueueItem((prev) => ({ ...prev, [messageId]: newItem.id }));

          // Auto-open thread to show bot response
          const openThreadId = freshThreadInfo?.threadId || messageId;
          openThread(openThreadId);

          // Add 🎟️ to the parent message
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? { ...msg, reactions: [...(msg.reactions || []), { emoji: "🎟️", count: 1 }] }
                : msg
            )
          );
        } else if (existingItem.assignedTo) {
          // Item already assigned - show error
          const threadId = freshThreadInfo?.threadId || messageId;
          setThreads((prev) => {
            if (!prev[threadId]) {
              return {
                ...prev,
                [threadId]: {
                  parentId: threadId,
                  replies: [
                    {
                      ...createBotMessage(
                        `This item is already assigned to ${existingItem.assignedToName || existingItem.assignedTo}`
                      ),
                    },
                  ],
                },
              };
            }
            return {
              ...prev,
              [threadId]: {
                ...prev[threadId],
                replies: [
                  ...prev[threadId].replies,
                  {
                    ...createBotMessage(
                      `This item is already assigned to ${existingItem.assignedToName || existingItem.assignedTo}`
                    ),
                  },
                ],
              },
            };
          });
          openThread(threadId);
        } else {
          // Item exists but not assigned - assign to you
          const itemIndex = queue.findIndex((q) => q.text === freshTargetMsg.text);
          if (itemIndex === -1) return;

          const assignMessage = createBotMessage(msgTaskAddedAndAssigned("You"));
          setQueue((prev) =>
            prev.map((item, i) =>
              i === itemIndex
                ? {
                    ...item,
                    assignedTo: "You",
                    assignedToName: "You",
                    assignMessageId: assignMessage.id,
                  }
                : item
            )
          );

          // Add ✅ emoji and bot message
          const threadId = freshThreadInfo?.threadId || messageId;
          setThreads((prev) => {
            if (!prev[threadId]) {
              return {
                ...prev,
                [threadId]: {
                  parentId: threadId,
                  replies: [assignMessage],
                },
              };
            }
            return {
              ...prev,
              [threadId]: {
                ...prev[threadId],
                replies: [
                  ...prev[threadId].replies.map((reply) =>
                    reply.id === messageId
                      ? {
                          ...reply,
                          reactions: [...(reply.reactions || []), { emoji: "✅", count: 1 }],
                        }
                      : reply
                  ),
                  assignMessage,
                ],
              },
            };
          });
          openThread(threadId);
        }
      })();
    }

    if (emoji === "🤚" && !hasReacted) {
      // Person raising hand emoji - trigger take logic
      (async () => {
        await delay(600);
        // Find the message in main channel or thread replies
        const freshMainMessage = messages.find((m) => m.id === messageId);
        let freshThreadInfo: { threadId: string; reply: Message } | null = null;

        if (!freshMainMessage) {
          for (const [tid, thread] of Object.entries(threads)) {
            const reply = thread.replies.find((r) => r.id === messageId);
            if (reply) {
              freshThreadInfo = { threadId: tid, reply };
              break;
            }
          }
        }

        const freshTargetMsg = freshMainMessage || freshThreadInfo?.reply;
        if (!freshTargetMsg) return;

        // Check if parent message is already in queue
        const existingItem = queue.find((q) => q.text === freshTargetMsg.text);

        if (!existingItem) {
          // Add parent message to queue and track the index
          const assignMessage = createBotMessage(msgTaskAddedAndAssigned("You"));
          const newItem: DemoQueueItem = {
            id: `queue-${Date.now()}`,
            text: freshTargetMsg.text,
            addedBy: freshTargetMsg.user,
            addedByName: freshTargetMsg.user,
            addedAt: new Date().toISOString(),
            done: false,
            assignMessageId: assignMessage.id,
            sourceMessageId: messageId,
            requestedBy: freshTargetMsg.user === "You" ? "user" : freshTargetMsg.user,
          };
          setQueue((prev) => {
            const newQueue = [...prev, newItem];
            const itemIndex = newQueue.length - 1;

            // Assign to you in the same update
            const assignedQueue = newQueue.map((item, i) =>
              i === itemIndex ? { ...item, assignedTo: "You", assignedToName: "You" } : item
            );

            // Add ✅ emoji and bot message
            const threadId = freshThreadInfo?.threadId || messageId;
            setThreads((prev) => {
              if (!prev[threadId]) {
                return {
                  ...prev,
                  [threadId]: {
                    parentId: threadId,
                    replies: [assignMessage],
                  },
                };
              }
              return {
                ...prev,
                [threadId]: {
                  ...prev[threadId],
                  replies: [
                    ...prev[threadId].replies.map((reply) =>
                      reply.id === messageId
                        ? {
                            ...reply,
                            reactions: [...(reply.reactions || []), { emoji: "✅", count: 1 }],
                          }
                        : reply
                    ),
                    assignMessage,
                  ],
                },
              };
            });

            return assignedQueue;
          });
          setMessageToQueueItem((prev) => ({ ...prev, [messageId]: newItem.id }));

          // Add 🎟️ to the parent message
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? { ...msg, reactions: [...(msg.reactions || []), { emoji: "🎟️", count: 1 }] }
                : msg
            )
          );
        } else {
          // Item already exists - find and assign it
          const itemIndex = queue.findIndex((q) => q.text === freshTargetMsg.text);
          if (itemIndex === -1) return;

          const assignMessage = createBotMessage(msgTaskAddedAndAssigned("You"));
          setQueue((prev) =>
            prev.map((item, i) =>
              i === itemIndex
                ? {
                    ...item,
                    assignedTo: "You",
                    assignedToName: "You",
                    assignMessageId: assignMessage.id,
                  }
                : item
            )
          );

          // Add ✅ emoji and bot message
          const threadId = freshThreadInfo?.threadId || messageId;
          setThreads((prev) => {
            if (!prev[threadId]) {
              return {
                ...prev,
                [threadId]: {
                  parentId: threadId,
                  replies: [assignMessage],
                },
              };
            }
            return {
              ...prev,
              [threadId]: {
                ...prev[threadId],
                replies: [
                  ...prev[threadId].replies.map((reply) =>
                    reply.id === messageId
                      ? {
                          ...reply,
                          reactions: [...(reply.reactions || []), { emoji: "✅", count: 1 }],
                        }
                      : reply
                  ),
                  assignMessage,
                ],
              },
            };
          });
        }
      })();
    }
  };

  const [, mainSendAction] = useActionState(async (_prevState: null, formData: FormData) => {
    const rawInput = String(formData.get("message") ?? "");
    if (!rawInput.trim()) return null;

    // Mark user as having interacted (triggers Neil's async request after 10s)
    if (!userHasInteracted) setUserHasInteracted(true);

    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      user: "You",
      userColor: "text-zinc-600",
      avatar: "Y",
      time: formatTime(new Date()),
      text: rawInput,
    };

    setMessages((prev) => [...prev, newMessage]);
    const input = rawInput;
    setMainInput("");

    // Process commands in main channel
    if (input.toLowerCase().startsWith("!add ")) {
      const item = input.replace(/!add\s+/i, "").trim();

      const pendingReply: Message = {
        id: `bot-pending-${Date.now()}`,
        user: "Kyubot",
        userColor: "text-orange-700",
        avatar: "/logo.webp",
        time: formatTime(new Date()),
        text: "Kyubot is thinking…",
        isBot: true,
      };

      setPendingThreadReply({ threadId: newMessage.id, reply: pendingReply });
      openThread(newMessage.id);

      setTimeout(() => {
        const botMessage = createBotMessage(msgAddedToQueue(item));
        const newItem: DemoQueueItem = {
          id: `queue-${Date.now()}`,
          text: item,
          addedBy: "You",
          addedByName: "You",
          addedAt: new Date().toISOString(),
          done: false,
          botMessageId: botMessage.id,
          sourceMessageId: newMessage.id,
          requestedBy: "user",
        };
        setQueue((prev) => [...prev, newItem]);
        setTotalItemsAdded((prev) => prev + 1);
        // Track mapping from message to queue item
        setMessageToQueueItem((prev) => ({ ...prev, [newMessage.id]: newItem.id }));
        // Add 🎟️ reaction to the user's message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === newMessage.id
              ? { ...msg, reactions: [...(msg.reactions || []), { emoji: "🎟️", count: 1 }] }
              : msg
          )
        );
        // Add bot message to thread
        setThreads((prev) => ({
          ...prev,
          [newMessage.id]: {
            parentId: newMessage.id,
            replies: [botMessage],
          },
        }));
        setPendingThreadReply(null);
        openThread(newMessage.id);
      }, 600);
    }

    if (input.toLowerCase() === "!list") {
      setTimeout(() => {
        const listText = formatQueueListSimple(queue);
        setMessages((prev) => [...prev, createBotMessage(listText)]);
      }, 600);
    }

    if (input.toLowerCase() === "!take") {
      setTimeout(() => {
        const unassignedIdx = findFirstUnassigned(queue);
        if (unassignedIdx !== -1) {
          const item = queue[unassignedIdx];
          const assignMessage = createBotMessage(msgTaskAddedAndAssigned("You"));
          setQueue((prev) =>
            prev.map((q, i) =>
              i === unassignedIdx
                ? {
                    ...q,
                    assignedTo: "You",
                    assignedToName: "You",
                    assignMessageId: assignMessage.id,
                  }
                : q
            )
          );
          // Post assignment message in the thread of the item's source message
          const threadId = item?.sourceMessageId || newMessage.id;
          setThreads((prev) => {
            const existingThread = prev[threadId];
            if (!existingThread) {
              return {
                ...prev,
                [threadId]: {
                  parentId: threadId,
                  replies: [assignMessage],
                },
              };
            }
            return {
              ...prev,
              [threadId]: {
                ...existingThread,
                replies: [...existingThread.replies, assignMessage],
              },
            };
          });
          openThread(threadId);
        } else {
          setMessages((prev) => [...prev, createBotMessage(MSG_NO_UNASSIGNED_ITEMS)]);
        }
      }, 600);
    }

    if (input.toLowerCase().startsWith("!done")) {
      const parts = input.trim().split(/\s+/);
      const arg = parts.slice(1).join(" ");
      (async () => {
        await delay(600);
        if (queue.length === 0) {
          setMessages((prev) => [...prev, createBotMessage(MSG_NO_PENDING_ITEMS)]);
          return;
        }

        const result = findItemByArg(queue, arg);

        if (!result.found) {
          let errorMsg = "";
          if (result.error === "not_found") {
            errorMsg = arg ? `No item in the queue matches "${arg}"` : MSG_NO_PENDING_ITEMS;
          } else if (result.error === "multiple_matches") {
            errorMsg = `Multiple items match "${arg}". Please be more specific.`;
          }

          setMessages((prev) => [...prev, createBotMessage(errorMsg)]);
          return;
        }

        const item = queue[result.index];

        // Use shared completion logic
        handleItemCompletion(item, newMessage.id);
      })();
    }

    // Handle !assign @name
    const assignMatch = input.match(/^!assign\s+@(\w+)/i);
    if (assignMatch) {
      const assigneeName = assignMatch[1];
      const displayName = assigneeName.charAt(0).toUpperCase() + assigneeName.slice(1);
      setTimeout(() => {
        const unassignedIdx = findFirstUnassigned(queue);
        if (unassignedIdx !== -1) {
          const item = queue[unassignedIdx];
          const assignMessage = createBotMessage(msgTaskAssigned(displayName));
          setQueue((prev) =>
            prev.map((q, i) =>
              i === unassignedIdx
                ? {
                    ...q,
                    assignedTo: displayName,
                    assignedToName: displayName,
                    assignMessageId: assignMessage.id,
                  }
                : q
            )
          );
          // Post assignment message in the thread of the item's source message
          const threadId = item?.sourceMessageId || newMessage.id;
          setThreads((prev) => {
            const existingThread = prev[threadId];
            if (!existingThread) {
              return {
                ...prev,
                [threadId]: {
                  parentId: threadId,
                  replies: [assignMessage],
                },
              };
            }
            return {
              ...prev,
              [threadId]: {
                ...existingThread,
                replies: [...existingThread.replies, assignMessage],
              },
            };
          });
          openThread(threadId);
        } else {
          setMessages((prev) => [...prev, createBotMessage(MSG_NO_UNASSIGNED_ITEMS)]);
        }
      }, 600);
    }

    if (input.toLowerCase() === "!help") {
      setTimeout(() => {
        setMessages((prev) => [...prev, createBotMessage(HELP_TEXT)]);
      }, 600);
    }

    // Scenario 1: User types a non-command message that looks like a request → Jayden reacts 🎟️ → Sarah takes it
    const isCommand = /^!(add|list|done|take|assign|help)(\s|$)/i.test(input);
    if (!isCommand && input.trim() && looksLikeRequest(input)) {
      // Jayden reacts with 🎟️ after 1 second
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === newMessage.id
              ? { ...msg, reactions: [...(msg.reactions || []), { emoji: "🎟️", count: 1 }] }
              : msg
          )
        );

        // Add to queue
        const botMessage = createBotMessage(msgAddedToQueue(input));
        const newItem: DemoQueueItem = {
          id: `queue-${Date.now()}`,
          text: input,
          addedBy: "You",
          addedByName: "You",
          addedAt: new Date().toISOString(),
          done: false,
          botMessageId: botMessage.id,
          sourceMessageId: newMessage.id,
          requestedBy: "user",
        };
        setQueue((prev) => [...prev, newItem]);
        setMessageToQueueItem((prev) => ({ ...prev, [newMessage.id]: newItem.id }));
        setTotalItemsAdded((prev) => prev + 1);

        // Kyubot confirms in thread
        setThreads((prev) => ({
          ...prev,
          [newMessage.id]: {
            parentId: newMessage.id,
            replies: [botMessage],
          },
        }));
        openThread(newMessage.id);

        // Random responder types and then says "!take I got this one"
        (async () => {
          const responders = currentScenario.responders;
          const taker = responders[Math.floor(Math.random() * responders.length)];
          await delay(1500);
          setThreadTypingUser(taker.name);

          await delay(1500);
          const takerMsg = createNpcMessage(
            taker.name,
            "!take I got this one",
            taker.avatar,
            taker.color
          );
          setThreads((prev) => ({
            ...prev,
            [newMessage.id]: {
              ...prev[newMessage.id],
              replies: [...prev[newMessage.id].replies, { ...takerMsg }],
            },
          }));
          setThreadTypingUser(null);
          await delay(1500);
          const assignMessage = createBotMessage(msgTaskAssigned(taker.name));
          setQueue((prev) =>
            prev.map((item) =>
              item.id === newItem.id
                ? {
                    ...item,
                    assignedTo: taker.name,
                    assignedToName: taker.name,
                    assignMessageId: assignMessage.id,
                  }
                : item
            )
          );
          setThreads((prev) => ({
            ...prev,
            [newMessage.id]: {
              ...prev[newMessage.id],
              replies: [
                ...prev[newMessage.id].replies.map((reply) =>
                  reply.id === takerMsg.id
                    ? { ...reply, reactions: [{ emoji: "✅", count: 1 }] }
                    : reply
                ),
                assignMessage,
              ],
            },
          }));
        })();
      }, 1000);
    }

    // Scenario 3: User types a casual message → channel responders randomly respond
    if (!isCommand && input.trim() && !looksLikeRequest(input)) {
      // Random chance to respond (70% to make it feel natural)
      if (Math.random() < 0.7) {
        const responders = currentScenario.responders;
        const responder = responders[Math.floor(Math.random() * responders.length)];
        const lowerInput = input.toLowerCase().trim();

        // Contextual responses based on message type
        let response = "";
        if (/^(hi|hey|hello|yo|sup)/.test(lowerInput)) {
          const greetings = ["Hey!", "Hi there!", "Hello!", "Hey!", "Hi!", "Hey!"];
          response = greetings[Math.floor(Math.random() * greetings.length)];
        } else if (/^(thanks|thank you|thx|ty)/.test(lowerInput)) {
          const thanksResponses = [
            "You're welcome!",
            "No problem!",
            "Anytime!",
            "Of course!",
            "Happy to help!",
          ];
          response = thanksResponses[Math.floor(Math.random() * thanksResponses.length)];
        } else if (/^(ok|okay|sure|np|no problem)/.test(lowerInput)) {
          const agreementResponses = ["Sounds good!", "Got it!", "Perfect!", "Alright!", "Cool!"];
          response = agreementResponses[Math.floor(Math.random() * agreementResponses.length)];
        } else if (/^(bye|goodbye|later|cya)/.test(lowerInput)) {
          const goodbyeResponses = ["See ya!", "Bye!", "Later!", "Take care!", "See you later!"];
          response = goodbyeResponses[Math.floor(Math.random() * goodbyeResponses.length)];
        } else if (/^(yes|no|yep|nope|yeah|nah)/.test(lowerInput)) {
          const yesNoResponses = {
            yes: ["Totally!", "For sure!", "Definitely!", "Absolutely!", "100%"],
            no: ["Nah", "Nope", "Not really", "Don't think so", "Probably not"],
          };
          const responses = lowerInput.includes("n") ? yesNoResponses.no : yesNoResponses.yes;
          response = responses[Math.floor(Math.random() * responses.length)];
        } else if (/(nice|cool|awesome|great|lol|haha|lmao)/.test(lowerInput)) {
          const reactionResponses = {
            nice: ["Thanks!", "Appreciate it!", "Glad you think so!"],
            cool: ["Right?", "I know!", "Totally!"],
            awesome: ["Thanks!", "Happy to help!", "Glad you like it!"],
            great: ["Thanks!", "Appreciate that!"],
            lol: ["haha", "lol", "😂", "haha right?"],
            haha: ["lol", "haha", "😂", "so true"],
          };
          const matchedKey = Object.keys(reactionResponses).find((key) => lowerInput.includes(key));
          if (matchedKey) {
            const responses = reactionResponses[matchedKey as keyof typeof reactionResponses];
            response = responses[Math.floor(Math.random() * responses.length)];
          }
        }

        // Fallback to generic casual responses if no specific match
        if (!response) {
          const genericResponses = [
            "nice!",
            "cool",
            "for sure",
            "yeah",
            "totally",
            "right?",
            "good point",
            "true",
            "definitely",
            "agree",
            "yep",
            "for real",
            "same here",
            "absolutely",
          ];
          response = genericResponses[Math.floor(Math.random() * genericResponses.length)];
        }

        setTimeout(
          () => {
            setMainTypingUser(responder.name);

            setTimeout(
              () => {
                setMainTypingUser(null);
                const responseMsg = createNpcMessage(
                  responder.name,
                  response,
                  responder.avatar,
                  responder.color
                );
                setMessages((prev) => [...prev, responseMsg]);
              },
              1000 + Math.random() * 1000
            ); // Random delay between 1-2 seconds
          },
          800 + Math.random() * 700
        ); // Random delay between 0.8-1.5 seconds
      }
    }

    // Scenario 2: After !list, a random responder types "!done 1" (only once)
    if (input.toLowerCase() === "!list" && !listCommandUsed && queue.length > 0) {
      setListCommandUsed(true);

      // Random responder types and sends "!done 1" (avoid picking the item's author)
      (async () => {
        const responders = currentScenario.responders;
        const firstItemAuthor = queue[0]?.addedByName;
        const eligible = responders.filter((r) => r.name !== firstItemAuthor);
        const pool = eligible.length > 0 ? eligible : responders;
        const responder = pool[Math.floor(Math.random() * pool.length)];

        await delay(1500);
        setMainTypingUser(responder.name);

        await delay(1000);
        setMainTypingUser(null);
        const responderMsg = createNpcMessage(
          responder.name,
          "!done 1",
          responder.avatar,
          responder.color
        );
        setMessages((prev) => [...prev, responderMsg]);

        await delay(600);
        if (queue.length > 0) {
          const item = queue[0];
          const responderMsgId = responderMsg.id;

          // Use shared completion logic (not a user action, so no celebration)
          handleItemCompletion(item, responderMsgId, false);
        }
      })();
    }
    return null;
  }, null);

  const [, threadSendAction] = useActionState(async (_prevState: null, formData: FormData) => {
    if (!activeThread) return null;
    const rawInput = String(formData.get("reply") ?? "");
    if (!rawInput.trim()) return null;

    // Mark user as having interacted (triggers Neil's async request after 10s)
    if (!userHasInteracted) setUserHasInteracted(true);

    const now = new Date();

    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      user: "You",
      userColor: "text-zinc-600",
      avatar: "Y",
      time: formatTime(now),
      text: rawInput,
    };

    setThreads((prev) => {
      if (!prev[activeThread]) {
        return {
          ...prev,
          [activeThread]: {
            parentId: activeThread,
            replies: [newMessage],
          },
        };
      }
      return {
        ...prev,
        [activeThread]: {
          ...prev[activeThread],
          replies: [...prev[activeThread].replies, newMessage],
        },
      };
    });

    if (rawInput.toLowerCase().includes("!add ")) {
      const item = rawInput.replace(/!add\s+/i, "").trim();
      setTimeout(() => {
        const botMessage = createBotMessage(msgAddedToQueue(item));
        const newItem = {
          id: `queue-${Date.now()}`,
          text: item,
          addedBy: "You",
          addedByName: "You",
          addedAt: new Date().toISOString(),
          done: false,
          botMessageId: botMessage.id,
          sourceMessageId: newMessage.id,
          requestedBy: "user",
        };
        setQueue((prev) => [...prev, newItem]);
        // Track mapping from message to queue item
        setMessageToQueueItem((prev) => ({ ...prev, [newMessage.id]: newItem.id }));
        // Add 🎟️ reaction to your message
        setThreads((prev) => {
          if (!prev[activeThread]) return prev;
          return {
            ...prev,
            [activeThread]: {
              ...prev[activeThread],
              replies: [
                ...prev[activeThread].replies.map((reply) =>
                  reply.id === newMessage.id
                    ? {
                        ...reply,
                        reactions: [...(reply.reactions || []), { emoji: "🎟️", count: 1 }],
                      }
                    : reply
                ),
                botMessage,
              ],
            },
          };
        });
      }, 600);
    }

    if (rawInput.toLowerCase().includes("!list")) {
      setTimeout(() => {
        const listText = formatQueueListSimple(queue);
        setThreads((prev) => {
          if (!prev[activeThread]) return prev;
          return {
            ...prev,
            [activeThread]: {
              ...prev[activeThread],
              replies: [
                ...prev[activeThread].replies,
                {
                  ...createBotMessage(listText),
                },
              ],
            },
          };
        });
      }, 600);
    }

    setThreadInput("");
    return null;
  }, null);

  const getReplyCount = (messageId: string) => threads[messageId]?.replies.length || 0;
  const parentMessage = activeThread ? messages.find((m) => m.id === activeThread) : null;

  // Get the timestamp of the last reply in the thread
  const getLastReplyTime = (messageId: string) => {
    const thread = threads[messageId];
    if (!thread || thread.replies.length === 0) return null;
    return thread.replies[thread.replies.length - 1].time;
  };

  const renderMessage = (msg: Message, compact = false) => (
    <article
      key={msg.id}
      className={`relative flex gap-2 group ${msg.isBot ? "bg-amber-50" : "hover:bg-slate-50"} px-2 md:px-3 py-1 -mx-2 rounded-lg transition-colors`}
      onClick={(e) => {
        if (longPressFiredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          longPressFiredRef.current = false;
          return;
        }
        handleMessageClick(e);
        if (window.innerWidth < 768 && !compact) {
          const target = e.target as HTMLElement;
          if (!target.closest("button") && !target.classList.contains("mention-link")) {
            openThread(msg.id);
          }
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && window.innerWidth < 768 && !compact) {
          openThread(msg.id);
        }
      }}
      onTouchStart={() => {
        longPressFiredRef.current = false;
        longPressTimerRef.current = setTimeout(() => {
          longPressFiredRef.current = true;
          setLongPressedMessage(msg.id);
        }, 500);
      }}
      onTouchEnd={() => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }}
      onTouchMove={() => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }}
      onMouseDown={() => {
        if (window.innerWidth < 768) {
          longPressFiredRef.current = false;
          longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            setLongPressedMessage(msg.id);
          }, 500);
        }
      }}
      onMouseUp={() => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }}
      onMouseLeave={() => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }}
    >
      <button
        type="button"
        className={`w-9 h-9 md:w-12 md:h-12 text-lg md:text-xl rounded-lg ${msg.isBot ? "bg-white" : "bg-slate-200 text-slate-600"} flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer`}
        onClick={() => openProfile(msg.user)}
        aria-label={`View ${msg.user}'s profile`}
      >
        {msg.avatar.startsWith("/") ? (
          <img src={msg.avatar} alt={msg.user} className="w-full h-full object-cover" />
        ) : msg.avatar === "N" ? (
          <div className="w-full h-full bg-blue-100 flex items-center justify-center text-blue-600">
            N
          </div>
        ) : msg.avatar === "S" ? (
          <div className="w-full h-full bg-emerald-100 flex items-center justify-center text-emerald-700">
            S
          </div>
        ) : (
          msg.avatar
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <button
            type="button"
            className={`font-bold text-[15px] ${msg.userColor} cursor-pointer hover:underline bg-transparent border-none p-0`}
            onClick={(e) => {
              e.stopPropagation();
              openProfile(msg.user);
            }}
          >
            {msg.user}
          </button>
          <span className="text-xs text-slate-500">{msg.time}</span>
        </div>
        <p
          className="text-slate-900 text-[15px] leading-relaxed mt-0.5"
          dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
        />

        {/* Reactions */}
        {msg.reactions && msg.reactions.length > 0 && (
          <div className="flex gap-1.5 mt-1">
            {msg.reactions.map((r) => {
              const hasUserReacted = userReactions.has(`${msg.id}-${r.emoji}`);
              return (
                <button
                  key={`${r.emoji}-${r.count}`}
                  type="button"
                  onClick={() => addReaction(msg.id, r.emoji)}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-all ${
                    hasUserReacted
                      ? "bg-blue-100 border border-blue-400 hover:bg-blue-50"
                      : "bg-[#f2f2f2] hover:bg-white hover:border-slate-300 border border-transparent"
                  }`}
                >
                  <span>{r.emoji}</span>
                  <span
                    className={`font-medium ${hasUserReacted ? "text-blue-600" : "text-slate-500"}`}
                  >
                    {r.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Thread link */}
        {!compact && getReplyCount(msg.id) > 0 && (
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              className="flex items-center gap-2 group/thread cursor-pointer"
              onClick={() => openThread(msg.id)}
            >
              <div className="flex items-center gap-1">
                {threads[msg.id]?.replies.some((r) => r.user === "Kyubot") && (
                  <div className="w-6 h-6 rounded overflow-hidden flex-shrink-0">
                    <img src="/logo.webp" alt="Kyubot" className="w-full h-full object-cover" />
                  </div>
                )}
                {threads[msg.id]?.replies.some((r) => r.user === "Sarah") && (
                  <div className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center text-sm text-emerald-700">
                    S
                  </div>
                )}
                {threads[msg.id]?.replies.some((r) => r.user === "Jayden") && (
                  <div className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center text-sm text-purple-600">
                    J
                  </div>
                )}
                {threads[msg.id]?.replies.some((r) => r.user === "Neil") && (
                  <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-sm text-blue-600">
                    N
                  </div>
                )}
                {threads[msg.id]?.replies.some((r) => r.user === "You") && (
                  <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-sm text-blue-600">
                    Y
                  </div>
                )}
              </div>
              <span className="text-sm text-blue-600 font-medium group-hover/thread:underline">
                {getReplyCount(msg.id)} {getReplyCount(msg.id) === 1 ? "reply" : "replies"}
              </span>
              <span className="text-xs text-slate-500 group-hover/thread:text-slate-500">
                {getLastReplyTime(msg.id) || "Today"}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Slack-style Action Toolbar - desktop hover overlay */}
      {!msg.isBot && (
        <div className="hidden md:flex p-1 absolute -top-3 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-sm border border-slate-200 bg-white rounded-lg items-center">
          <button
            type="button"
            onClick={() => addReaction(msg.id, "🎟️")}
            className="p-1.5 hover:bg-amber-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="Add to queue"
            aria-label="Add to queue"
          >
            <span className="">🎟️</span>
          </button>
          <button
            type="button"
            onClick={() => addReaction(msg.id, "✅")}
            className="p-1.5 hover:bg-amber-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="Mark as done"
            aria-label="Mark as done"
          >
            <span className="">✅</span>
          </button>
          <button
            type="button"
            onClick={() => addReaction(msg.id, "🙋")}
            className="p-1.5 hover:bg-amber-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="Take this item"
            aria-label="Take this item"
          >
            <span className="">🙋</span>
          </button>
          <button
            type="button"
            className="p-1.5 hover:bg-slate-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="Find another reaction"
            aria-label="Find another reaction"
          >
            <SmilePlus className="w-4 h-4" />
          </button>
          {!compact && (
            <button
              type="button"
              className="p-1.5 hover:bg-slate-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
              title="Reply to thread"
              onClick={() => openThread(msg.id)}
              aria-label="Reply to thread"
            >
              <MessageSquareText className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            className="p-0.5 hover:bg-slate-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="Forward message..."
            aria-label="Forward message"
          >
            <ArrowBigRight className="w-6 h-6 stroke-[1.25]" />
          </button>
          <button
            type="button"
            className="p-1.5 hover:bg-slate-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="Save for later"
            aria-label="Save for later"
          >
            <Bookmark className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="p-1.5 hover:bg-slate-100 text-slate-500 transition-colors font-medium text-xs rounded-lg"
            title="More actions"
            aria-label="More actions"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      )}
    </article>
  );

  // Reset thread when switching channels
  const handleChannelSwitch = (channelId: string) => {
    startTransition(() => {
      setActiveThread(null);
      setThreadInput("");
      setMainInput("");
      setMainTypingUser(null);
      setThreadTypingUser(null);
      setActiveChannel(channelId);
      setViewedChannels((prev) => new Set(prev).add(channelId));
      setMobileView("chat");
    });
  };

  // On mobile, thread/profile are full-screen overlays
  const mobileShowThread = activeThread && !activeProfile && parentMessage;
  const mobileShowProfile = !!activeProfile;

  return (
    <div className="flex justify-center md:block">
      {/* iPhone frame - visible on mobile, transparent on desktop */}
      <div className="bg-black rounded-[3rem] p-[6px] shadow-2xl max-w-[340px] w-full md:max-w-none md:rounded-none md:p-0 md:shadow-none md:bg-transparent relative">
        <div className="bg-white rounded-[2.5rem] md:rounded-2xl shadow-xl border-0 md:border md:border-slate-200 overflow-hidden relative">
          {/* Dynamic Island - floats over screen content, mobile only */}
          <div className="md:hidden absolute top-1 left-1/2 -translate-x-1/2 z-40">
            <div className="w-[100px] h-[28px] bg-black rounded-full flex items-center justify-center">
              <div className="w-2.5 h-2.5 rounded-full bg-[#1a1a1a] border border-[#333]"></div>
            </div>
          </div>
          {/* Window chrome - desktop only */}
          <div className="hidden md:flex bg-slate-800 px-4 py-2.5 items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <span className="text-slate-300 text-sm font-medium ml-2">Slack</span>
          </div>

          {/* Main content */}
          <div className="flex h-[680px] md:h-[500px] relative">
            {/* Desktop sidebar */}
            <div className="hidden md:flex w-48 bg-[#4A154B] flex-col border-r border-[#5E2C5F]">
              <div className="px-3 py-3 border-b border-[#5E2C5F]">
                <span className="font-bold text-white text-sm">Kyubot</span>
              </div>
              <div className="flex-1 overflow-y-auto py-2">
                <div className="px-3 mb-2">
                  <span className="text-xs font-medium text-[#D1C4D1] uppercase tracking-wide">
                    Channels
                  </span>
                </div>
                {CHANNEL_ORDER.map((channelId) => {
                  const scenario = ALL_SCENARIOS[channelId];
                  const isActive = channelId === activeChannel;
                  return (
                    <button
                      key={channelId}
                      type="button"
                      onClick={() => {
                        handleChannelSwitch(channelId);
                      }}
                      className={`w-full px-3 py-1 text-left flex items-center gap-2 text-sm transition-colors ${
                        isActive
                          ? "bg-[#1264A3] text-white"
                          : viewedChannels.has(channelId)
                            ? "text-[#D1C4D1] hover:bg-[#5E2C5F]"
                            : "text-white font-bold hover:bg-[#5E2C5F]"
                      }`}
                    >
                      <Hash className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{scenario.channelName}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mobile: Home tab - channel list with bottom tab bar */}
            {mobileView === "home" && (
              <div className="md:hidden absolute inset-0 z-30 bg-white flex flex-col">
                {/* Home header - purple */}
                <div className="px-4 pt-10 pb-2 bg-[#4A154B]">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-xl">Kyubot</span>
                  </div>
                </div>
                {/* Channel list - white background */}
                <div className="flex-1 overflow-y-auto bg-white">
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Channels
                    </span>
                  </div>
                  {CHANNEL_ORDER.map((channelId) => {
                    const scenario = ALL_SCENARIOS[channelId];
                    const hasUnread = !viewedChannels.has(channelId);
                    return (
                      <button
                        key={channelId}
                        type="button"
                        onClick={() => {
                          handleChannelSwitch(channelId);
                        }}
                        className="w-full px-4 py-2.5 text-left flex items-center gap-3 active:bg-slate-100 transition-colors"
                      >
                        <Hash className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span
                            className={`text-[15px] ${hasUnread ? "font-bold text-slate-900" : "text-slate-600"} truncate block`}
                          >
                            {scenario.channelName}
                          </span>
                        </div>
                        {hasUnread && (
                          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Bottom tab bar */}
                <div className="border-t border-slate-200 bg-white px-2 pb-1">
                  <div className="flex items-center justify-around py-1.5">
                    <button
                      type="button"
                      className="flex flex-col items-center gap-0.5 px-3 py-1 text-[#4A154B]"
                    >
                      <Home className="w-5 h-5" />
                      <span className="text-[10px] font-medium">Home</span>
                    </button>
                    <button
                      type="button"
                      className="flex flex-col items-center gap-0.5 px-3 py-1 text-slate-400"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span className="text-[10px] font-medium">DMs</span>
                    </button>
                    <button
                      type="button"
                      className="flex flex-col items-center gap-0.5 px-3 py-1 text-slate-400"
                    >
                      <Bell className="w-5 h-5" />
                      <span className="text-[10px] font-medium">Activity</span>
                    </button>
                    <button
                      type="button"
                      className="flex flex-col items-center gap-0.5 px-3 py-1 text-slate-400"
                    >
                      <Search className="w-5 h-5" />
                      <span className="text-[10px] font-medium">Search</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Main channel - hidden on mobile when thread or profile is open */}
            <div
              className={`flex flex-col bg-white flex-1 min-w-0 ${
                activeThread ? "md:border-r md:border-slate-200" : ""
              } ${mobileShowThread || mobileShowProfile ? "hidden md:flex" : "flex"}`}
            >
              {/* Channel header - mobile: white with back arrow, desktop: plain */}
              <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 md:pt-2 pt-8">
                <button
                  type="button"
                  onClick={() => setMobileView("home")}
                  className="md:hidden p-1 -ml-1 text-slate-600 active:text-slate-800 flex items-center gap-0.5"
                  aria-label="Back to Home"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                {/* Desktop channel name */}
                <span className="hidden md:inline font-semibold text-slate-800 text-[15px]">
                  # {currentScenario.channelName}
                </span>
                {/* Mobile channel name */}
                <span className="md:hidden font-semibold text-slate-900 text-[15px]">
                  # {currentScenario.channelName}
                </span>
              </div>

              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-2 space-y-1">
                {messages.map((msg) => (
                  <div key={msg.id} id={`msg-${msg.id}`}>
                    {renderMessage(msg)}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Main channel input */}
              <form action={mainSendAction} className="px-3 md:px-4 py-2 gap-2">
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    name="message"
                    value={mainInput}
                    onChange={(e) => setMainInput(e.target.value)}
                    placeholder={`Message #${currentScenario.channelName}`}
                    className="flex-1 px-2 py-1.5 rounded-lg border border-slate-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none text-sm"
                  />
                  <MainSendButton disabled={!mainInput.trim()} />
                </div>
                <div className="px-2 text-[0.625rem] text-slate-500 italic">
                  {mainTypingUser ? (
                    <>
                      <strong>{mainTypingUser}</strong> is typing
                    </>
                  ) : (
                    <>&nbsp;</>
                  )}
                </div>
              </form>
            </div>

            {/* Profile panel - desktop: side panel, mobile: full-screen overlay */}
            {activeProfile && (
              <div className="absolute inset-0 z-20 md:static md:inset-auto md:z-auto md:w-72 flex-shrink-0 flex flex-col bg-white md:border-l border-slate-200">
                <div className="px-3 pt-8 md:pt-2 py-2 flex items-center justify-between border-b border-slate-200 md:border-b-0">
                  <div className="flex items-center gap-2">
                    {activeThread && (
                      <button
                        type="button"
                        onClick={backToThread}
                        className="p-1 text-slate-500 hover:text-slate-600 -ml-1"
                        title="Back to thread"
                        aria-label="Back to thread"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                    )}
                    <span className="font-semibold text-slate-800 text-sm">Profile</span>
                  </div>
                  <button
                    type="button"
                    onClick={closeProfile}
                    className="p-1 text-slate-500 hover:text-slate-600"
                    aria-label="Close profile"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {(() => {
                    const profile = USER_PROFILES[activeProfile];
                    if (!profile) return null;
                    return (
                      <div className="space-y-4">
                        <div className="flex flex-col items-center">
                          <div className="w-36 h-36 rounded-lg overflow-hidden bg-white mb-3">
                            {profile.avatar.startsWith("/") ? (
                              <img
                                src={profile.avatar}
                                alt={profile.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div
                                className={`w-full h-full flex items-center justify-center text-2xl font-bold ${profile.color.replace("text-", "bg-").replace("600", "100")} ${profile.color}`}
                              >
                                {profile.avatar}
                              </div>
                            )}
                          </div>
                          <h2 className={`font-bold text-lg ${profile.color}`}>{profile.name}</h2>
                          <p className="text-sm text-slate-500">{profile.title}</p>
                        </div>
                        <div className="border-t border-slate-200 pt-3">
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">
                            About
                          </p>
                          <p className="text-sm text-slate-700 leading-relaxed">{profile.bio}</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Thread panel - desktop: side panel, mobile: full-screen overlay */}
            {activeThread && !activeProfile && parentMessage && (
              <div className="absolute inset-0 z-20 md:static md:inset-auto md:z-auto md:w-72 flex-shrink-0 flex flex-col bg-white md:border-l border-slate-200">
                <div className="px-3 pt-8 md:pt-2 py-2 flex items-center justify-between border-b border-slate-200 md:border-b-0">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openThread(null)}
                      className="md:hidden p-1 text-slate-600 active:text-slate-800 -ml-1"
                      aria-label="Back to channel"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <span className="font-semibold text-slate-800 text-[15px]">Thread</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => openThread(null)}
                    className="hidden md:block p-1 text-slate-500 hover:text-slate-600"
                    aria-label="Close thread"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div ref={threadContainerRef} className="flex-1 overflow-y-auto px-2 space-y-1">
                  {/* Parent message in thread */}
                  <div className="border-b border-slate-200 pb-2 mb-2">
                    {renderMessage(parentMessage, true)}
                  </div>
                  {/* Replies */}
                  {(threads[activeThread]?.replies ?? []).map((reply) =>
                    renderMessage(reply, true)
                  )}
                  {pendingThreadReply?.threadId === activeThread
                    ? renderMessage(pendingThreadReply.reply, true)
                    : null}
                  <div ref={threadEndRef} />
                </div>

                {/* Thread input */}
                <form action={threadSendAction} className="p-2 bg-white">
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      name="reply"
                      value={threadInput}
                      onChange={(e) => setThreadInput(e.target.value)}
                      placeholder="Reply..."
                      className="flex-1 px-2 py-1.5 rounded-lg border border-slate-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none text-sm"
                    />
                    <ThreadSendButton disabled={!threadInput.trim()} />
                  </div>
                  <div className="px-2 text-[0.625rem] text-slate-500 italic">
                    {threadTypingUser ? (
                      <>
                        <strong>{threadTypingUser}</strong> is typing
                      </>
                    ) : (
                      <>&nbsp;</>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Long-press bottom sheet modal - mobile only */}
          {longPressedMessage && (
            <button
              type="button"
              className="md:hidden absolute inset-0 z-50 flex flex-col justify-end bg-transparent border-none p-0 cursor-default"
              onClick={() => setLongPressedMessage(null)}
              onKeyDown={(e) => {
                if (e.key === "Escape" || e.key === "Enter") {
                  setLongPressedMessage(null);
                }
              }}
              aria-label="Close reaction picker"
            >
              <div className="absolute inset-0 bg-black/30" />
              <div
                className="relative bg-white rounded-t-2xl shadow-2xl px-4 pt-3 pb-6 space-y-3"
                role="dialog"
                aria-label="Reaction picker"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <div className="flex justify-center mb-1">
                  <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
                </div>
                <div className="flex gap-3 justify-center">
                  {["🎟️", "✅", "🙋", "👍", "👀", "🎉"].map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        addReaction(longPressedMessage, emoji);
                        setLongPressedMessage(null);
                      }}
                      className="w-10 h-10 rounded-full bg-slate-100 active:bg-amber-100 flex items-center justify-center text-lg transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <div className="border-t border-slate-200 pt-2 space-y-1">
                  <button
                    type="button"
                    onClick={() => {
                      openThread(longPressedMessage);
                      setLongPressedMessage(null);
                    }}
                    className="w-full px-3 py-2.5 text-left text-[15px] text-slate-700 active:bg-slate-100 rounded-lg flex items-center gap-3"
                  >
                    <MessageSquareText className="w-5 h-5 text-slate-400" />
                    Reply in thread
                  </button>
                  <button
                    type="button"
                    onClick={() => setLongPressedMessage(null)}
                    className="w-full px-3 py-2.5 text-left text-[15px] text-slate-700 active:bg-slate-100 rounded-lg flex items-center gap-3"
                  >
                    <Bookmark className="w-5 h-5 text-slate-400" />
                    Save for later
                  </button>
                </div>
              </div>
            </button>
          )}

          {/* Home indicator - mobile only */}
          <div className="md:hidden flex justify-center py-2">
            <div className="w-32 h-1 bg-slate-300 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
